import { Api } from './axios-config';
import { IUserView } from './userService';

export interface ILogin {
  email?: string,
  phone?: string,
  password: string,
}

const login = async (body: ILogin): Promise<IUserView | Error> => {
  try {
    const { data } = await Api.post<IUserView | Error>(
      '/auth/login',
      body
    );
    return data;
  } catch (error: unknown) {
    throw new Error(error instanceof Error ? error.message : String(error));
  }
};

const googleLogin = async (jwt: string): Promise<IUserView | Error> => {
  try {
    const { data } = await Api.post<IUserView | Error>(
      '/auth/google-login',
      { token: jwt }
    );
    return data;
  } catch (error: unknown) {
    throw new Error(error instanceof Error ? error.message : String(error));
  }
};

const logout = async (): Promise<void> => {
  try {
    await Api.post('/auth/logout');
  } catch (error: unknown) {
    throw new Error(error instanceof Error ? error.message : String(error));
  }
};

export interface ICreateUser {
  name: string,
  email: string,
  password: string,
  passwordConfirmation: string,
  phone?: string,
  cpf?: string
}

const create = async (body: ICreateUser): Promise<IUserView | Error> => {
  try {
    const { data } = await Api.post<IUserView | Error>(
      '/auth/create',
      body
    );
    return data;
  } catch (error: unknown) {
    throw new Error(error instanceof Error ? error.message : String(error));
  }
};

interface ICreateClient {
  name: string;
  email?: string;
  phone?: string;
  businessId: number;
}

const createClient = async (body: ICreateClient): Promise<IUserView | null> => {
  try {
    const { data } = await Api.post<IUserView | null>(
      '/auth/create-client',
      body
    );
    return data;
  } catch (error: unknown) {
    throw new Error(error instanceof Error ? error.message : String(error));
  }
};

interface IChangePassword {
  oldPassword: string;
  newPassword: string;
}

const changePassword = async (body: IChangePassword): Promise<void> => {
  try {
    await Api.post('/auth/change-password', body);
  } catch (error: unknown) {
    throw new Error(error instanceof Error ? error.message : String(error));
  }
};

const getUserFromCookie = async (): Promise<IUserView> => {
  try {
    const { data } = await Api.get<IUserView>('/auth');
    return data;
  } catch (error: unknown) {
    throw new Error(error instanceof Error ? error.message : String(error));
  }
};

interface IForgotPassword {
  email?: string;
  phone?: string;
}

const forgotPassword = async (body: IForgotPassword): Promise<void> => {
  try {
    await Api.post('/auth/forgot-password', body);
  } catch (error: unknown) {
    throw new Error(error instanceof Error ? error.message : String(error));
  }
};

interface IResetPassword {
  token: string;
  newPassword: string;
  passwordConfirmation: string;
}

const resetPassword = async (body: IResetPassword): Promise<void> => {
  try {
    await Api.post('/auth/reset-password', body);
  } catch (error: unknown) {
    throw new Error(error instanceof Error ? error.message : String(error));
  }
};

interface IRequestPhoneConfirmation {
  phone: string;
}

const requestPhoneConfirmation = async (body: IRequestPhoneConfirmation): Promise<void> => {
  try {
    await Api.post('/user/request-phone-confirmation', body);
  } catch (error: unknown) {
    throw new Error(error instanceof Error ? error.message : String(error));
  }
};

interface IConfirmPhone {
  phone: string;
  code: string;
}

const confirmPhone = async (body: IConfirmPhone): Promise<void> => {
  try {
    await Api.post('/user/confirm-phone', body);
  } catch (error: unknown) {
    throw new Error(error instanceof Error ? error.message : String(error));
  }
};

export const authService = {
  login,
  googleLogin,
  logout,
  create,
  createClient,
  changePassword,
  getUserFromCookie,
  forgotPassword,
  resetPassword,
  requestPhoneConfirmation,
  confirmPhone,
};
